<template>
    <div>
        <CreateAccidentDialog
            :accident="accident"
            :is-open="createAccidentDialog"
            :action-loading="actionLoading.createAccident"
            @create-accident="createAccident"
            @close-accident-dialog="closeCreateAccidentDialog"
        />
        <AmountToReportDialog
            :is-open="amountToReportDialog"
            :report-data="report"
            :report-status="statusId"
            :allows-report-excess-amount="allowsReportExcessAmount"
            @on-save="saveAmountToReport"
            @on-close="closeAmountToReportDialog"
        />
        <MakeReadyAmountToReportDialog
            :is-open="makeReadyAmountToReportDialog"
            :report-data="report"
            :report-status="statusId"
            :allows-report-excess-amount="allowsReportExcessAmount"
            @on-save="saveMakeReadyAmountToReport"
            @on-close="closeMakeReadyAmountToReportDialog"
        />
        <ReportBreakCommentCodeDialog
            :is-open="reportBreakCommentCodeDialog"
            :report-data="report"
            :comment-codes="commentCodes"
            :action-loading="actionLoading.createBreakReportCommentCode"
            @on-save="saveBreakReportCommentCode"
            @on-close="closeBreakReportCommentCodeDialog"
        />
        <ReportFinishCommentCodeDialog
            :is-open="reportFinishCommentCodeDialog"
            :report-data="report"
            :comment-codes="commentCodes"
            :code-type-id="codeTypeId"
            @on-save="saveFinishReportCommentCode"
            @on-close="closeFinishReportCommentCodeDialog"
        />
        <OrderAdditionalBoardsDialog
            v-if="report.objectDetail.reportItemId"
            :is-open="openOrderAdditionalBoardsDialog"
            :machine-code="workspaceSettings.machineCode"
            :report-data="reportData"
            @on-close="closeOrderAdditionalBoardsDialog"
        />
        <ConfirmDialog
            :is-open="confirmFinishReportDialog"
            @on-confirm="stopTracking"
            @on-abort="closeConfirmFinishReportDialog"
        >
            <template #title>
                Wprowadzono pełny nakład.
            </template>
            <template #content>
                Czy na pewno chcesz przerwać raportowanie?
            </template>
        </ConfirmDialog>
        <ConfirmDialog
            :is-open="confirmNextReportCreationDialog"
            :action-loading="actionLoading.createReport"
            @on-confirm="createReport"
            @on-abort="closeConfirmNextReportCreationDialog"
        >
            <template #title>
                Znaleziono niezaraportowany montaż dla tego zlecenia.
            </template>
            <template #content>
                Czy chcesz zaraportować kolejny montaż?
            </template>
            <template #abortButton>
                Nie
            </template>
        </ConfirmDialog>

        <PalletsDialogOffsetReporting
            :is-open="palletsListOffsetDialog"
            :pallets-list="addedPaperPallets"
            :report-id="report.id"
            @on-close="closePalletsListDialog"
        />
        <PalletsDialog
            :is-open="palletsListDialog"
            :pallets-list="addedPaperPallets"
            :report-id="report.id"
            @on-close="closePalletsListDialog"
        />
        <ConfirmDialog
            :is-open="chooseFinishStepDialog"
            @on-confirm="handleFinishStepYesAction"
            @on-abort="handleFinishStepNoAction"
        >
            <template #title>
                Wybierz kolejny krok
            </template>
            <template #content>
                Czy chcesz przejść do kolejnego zadania w planie:
                <i v-if="reportData.nextScheduledTask">
                    {{ reportData.nextScheduledTask.procNumber }}, montaż: {{ reportData.nextScheduledTask.procPlateNumber }}
                </i>
            </template>
            <template #abortButton>
                Nie
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="openOffsetNextPlateToReportDialog"
            @on-confirm="handleOffsetNextPlateToReportYesAction"
            @on-abort="closeOffsetNextPlateToReportDialog"
        >
            <template #title>
                Czy chcesz przejść do kolejnego zadania w planie:
            </template>
            <template #content>
                <i v-if="offsetNextPlateToReport">
                    {{ offsetNextPlateToReport.primaryNumber }}, montaż: {{ offsetNextPlateToReport.plateNumber }}
                </i>
            </template>
            <template #abortButton>
                Nie
            </template>
        </ConfirmDialog>

        <v-container class="pa-0">
            <v-row>
                <v-col v-if="!isProcessReport" cols="12">
                    <v-container class="py-0">
                        <v-row>
                            <v-col cols="12">
                                <v-form class="qr-code-container" @submit.prevent="showAmountToReportDialog(99)">
                                    <img src="@/assets/images/qr/qr-tracking-stop.svg" class="qr-code hidden-sm-and-down mb-4">
                                    <v-row justify="center">
                                        <v-col>
                                            <v-text-field
                                                v-model.number="machineStuffAmount"
                                                type="number"
                                                min="1"
                                                label="Ilość osób"
                                                :error-messages="machineStuffAmountErrors"
                                                :disabled="isReportStopped"
                                                required
                                                dense
                                                outlined
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row justify="center">
                                        <v-col v-if="isPalletNumberRequired" cols="auto" class="d-flex">
                                            <v-badge
                                                :content="addedPaperPalletsCount"
                                                :color="hasNoPallets ? 'warning' : 'success'"
                                                overlap
                                            >
                                                <v-btn
                                                    data-sentry-label="Pokaż okno palet z papierem na aktywnym raporcie"
                                                    @click="showPalletsListDialog"
                                                >
                                                    <span> Papier </span>
                                                </v-btn>
                                            </v-badge>
                                        </v-col>
                                        <v-col cols="auto" class="d-flex">
                                            <v-btn
                                                v-if="[currentReportStates[1], currentReportStates[2]].includes(reportState)"
                                                color="error"
                                                :loading="stopTrackingLoading"
                                                :disabled="stopReportLoading"
                                                data-sentry-label="Zakończ przyrząd"
                                                @click="openEndMakeReadyDialog()"
                                            >
                                                {{ currentlyReporting }}
                                            </v-btn>
                                            <v-btn
                                                v-else
                                                color="error"
                                                :loading="stopTrackingLoading"
                                                :disabled="stopReportLoading"
                                                data-sentry-label="Zakończ"
                                                @click="isReportStopped ? endStoppedReport(99) : showAmountToReportDialog(99)"
                                            >
                                                {{ currentlyReporting }}
                                            </v-btn>
                                        </v-col>
                                        <v-col
                                            v-if="!isReportStopped && reportState === currentReportStates[1]"
                                            cols="auto"
                                            class="d-flex"
                                        >
                                            <v-btn
                                                color="primary"
                                                :loading="stopReportLoading"
                                                :disabled="stopTrackingLoading"
                                                data-sentry-label="Przerwij przyrząd"
                                                @click="stopMakeReady()"
                                            >
                                                Przerwij przyrząd
                                            </v-btn>
                                        </v-col>
                                        <v-col v-else-if="!isReportStopped" cols="auto" class="d-flex">
                                            <v-btn
                                                color="primary"
                                                :loading="stopReportLoading"
                                                :disabled="stopTrackingLoading"
                                                data-sentry-label="Przerwij"
                                                @click="showAmountToReportDialog(90)"
                                            >
                                                Przerwij
                                            </v-btn>
                                        </v-col>
                                        <v-col
                                            v-if="isReportStopped && reportState === currentReportStates[2]"
                                            cols="auto"
                                            class="d-flex"
                                        >
                                            <v-btn
                                                color="primary"
                                                :loading="renewReportLoading"
                                                :disabled="stopTrackingLoading || stopReportLoading"
                                                data-sentry-label="Wznów przyrząd"
                                                @click="renewReport(true)"
                                            >
                                                Wznów przyrząd
                                            </v-btn>
                                        </v-col>
                                        <v-col v-else-if="isReportStopped" cols="auto" class="d-flex">
                                            <v-btn
                                                color="primary"
                                                :loading="renewReportLoading"
                                                :disabled="stopTrackingLoading || stopReportLoading"
                                                data-sentry-label="Wznów"
                                                @click="renewReport()"
                                            >
                                                Wznów
                                            </v-btn>
                                        </v-col>
                                        <v-col
                                            v-if="!isReportStopped && isFromReporting && hasSameWorkspace && isNextStepVisible"
                                            cols="auto"
                                            class="d-flex"
                                        >
                                            <v-btn
                                                color="primary"
                                                outlined
                                                data-sentry-label="Nadaj"
                                                :disabled="!canShipOrder || stopTrackingLoading || stopReportLoading"
                                                @click="showOrders()"
                                            >
                                                Nadaj
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                    <v-row v-if="isOffsetPrintSite">
                                        <v-col cols="auto" class="d-flex">
                                            <v-btn
                                                color="success"
                                                data-sentry-label="Zamów plyty dodatkowe"
                                                @click="showOrderAdditionalBoardsDialog"
                                            >
                                                Zamów płyty dodatkowe
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                    <v-row v-if="hasBlackBoxIp">
                                        <v-col cols="auto" class="d-flex">
                                            <v-btn
                                                :color="blackBoxCounterOn ? 'error' : 'success'"
                                                :data-sentry-label="blackBoxCounterOn ? 'Zatrzymaj liczenie' : 'Wznów liczenie'"
                                                @click="pauseResumeBlackBoxCounting(blackBoxCounterOn ? false : true)"
                                            >
                                                {{ blackBoxCounterOn ? 'Zatrzymaj liczenie' : 'Wznów liczenie' }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-form>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import ConfirmDialog from '@/components/common/ConfirmDialog'
import AmountToReportDialog from './AmountToReportDialog'
import ReportBreakCommentCodeDialog from './ReportBreakCommentCodeDialog'
import ReportFinishCommentCodeDialog from './ReportFinishCommentCodeDialog'
import PalletsDialogOffsetReporting from '../Reporting/PalletsDialogOffsetReporting'
import PalletsDialog from '../Reporting/PalletsDialog'
import CreateAccidentDialog from '@/views/Maintenance/Accidents/CreateAccidentDialog'
import MakeReadyAmountToReportDialog from './MakeReadyAmountToReportDialog'
import OrderAdditionalBoardsDialog from './OrderAdditionalBoardsDialog'

import { minValue, required } from 'vuelidate/lib/validators'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'
import decodeQRcode from '@/mixins/decodeQRcode'
import filters from '@/mixins/dataTableFilters'
import customFilters from '@/mixins/customFilters'
import { EventBus } from '@/utils/eventBus'

export default {
    name: 'ReportData',
    components: {
        ReportBreakCommentCodeDialog,
        AmountToReportDialog,
        ReportFinishCommentCodeDialog,
        ConfirmDialog,
        CreateAccidentDialog,
        PalletsDialogOffsetReporting,
        PalletsDialog,
        OrderAdditionalBoardsDialog,
        MakeReadyAmountToReportDialog
    },
    mixins: [
        customFilters,
        tooltipDelay,
        decodeQRcode,
        parse,
        filters
    ],
    props: {
        reportData: {
            type: Object,
            default: null
        },
        allowsSendingOrders: {
            type: Boolean
        },
        allowsReportExcessAmount: {
            type: Boolean
        },
        routeParentName: {
            type: String,
            default: null
        },
        reportAmountData: {
            type: Number,
            default: null
        },
        machineStuffAmountData: {
            type: Number,
            default: null
        },
        maxReportAmountData: {
            type: Number,
            default: null
        },
        currentlyReportedQuantityData: {
            type: Number,
            default: null
        },
        delay: {
            type: Number,
            default: null
        },
        timeWithoutLimit: {
            type: Number,
            default: null
        },
        isFromReporting: {
            type: Boolean
        },
        workspaceId: {
            type: Number,
            default: null
        },
        additionalActionProcessProductionSite: {
            type: Array,
            default: () => []
        },
        isPalletNumberRequired: {
            type: Boolean
        },
        isNextStepVisible: {
            type: Boolean
        },
        productionSiteData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            report: {
                statusId: null,
                objectDetail: {
                    reportItemPlateParameters: []
                },
                comments: []
            },
            maxReportAmount: null,
            nextUnreportedPlate: null,
            stopTrackingLoading: false,
            actionLoading: {
                createReport: false,
                createAccident: false,
                createBreakReportCommentCode: false,
            },
            confirmNextReportCreationDialog: false,
            machineStuffAmount: null,
            reportBreakCommentCodeDialog: false,
            reportFinishCommentCodeDialog: false,
            openOrderAdditionalBoardsDialog: false,
            chooseFinishStepDialog: false,
            codeTypeId: null,
            commentCodes: [],
            statusId: null,
            confirmFinishReportDialog: false,
            stopReportLoading: false,
            renewReportLoading: false,
            endMakeReadyLoading: false,
            amountToReportDialog: false,
            makeReadyAmountToReportDialog: false,
            amountToReportAction: null,
            returnToSamePage: false,
            reopenAmountToReport: false,
            address: null,
            createAccidentDialog: false,
            palletsListDialog: false,
            palletsListOffsetDialog: false,
            accident: {
                id: null,
                machineId: null,
                accidentTypeId: null,
                description: null,
                accidentDepartmentId: null,
                username: null
            },
            selectedComment: null,
            currentReportStates: {
                0: 'FINISHED',
                1: 'MAKE_READY_IN_PROGRESS',
                2: 'MAKE_READY_BREAK',
                3: 'MAIN_REPORT_IN_PROGRESS',
                4: 'MAIN_REPORT_BREAK'
            },
            pallets: [],
            blackBoxCounterOn: false,
            offsetNextPlateToReport: null,
            openOffsetNextPlateToReportDialog: false
        }
    },
    validations() {
        return {
            machineStuffAmount: {
                required,
                minValue: minValue(1)
            },
        }
    },
    computed: {
        hasBlackBoxIp() {
            return Boolean(
                this.report &&
                this.report.productionSite &&
                this.report.productionSite.blackBoxIp &&
                this.report.productionSite.showBlackBoxOutputHint === true
            )
        },
        addedPaperPallets() {
            return this.report ? this.report.reportPallets : []
        },
        addedPaperPalletsCount() {
            if (!this.addedPaperPallets) {
                return '0'
            }

            return this.addedPaperPallets.length.toString()
        },
        hasNoPallets() {
            if (!this.addedPaperPallets) {
                return true
            }

            return this.addedPaperPallets.length < 1
        },
        userId() {
            return this.$store.state.user.userId
        },
        hasMultiplePlates() {
            return this.report.objectDetail.totalPlates > 1
        },
        isProcessReport() {
            return this.report.objectType === 3
        },
        isReportFinished() {
            if (this.report.statusId) {
                return this.report.statusId == 99
            }

            return false
        },
        isReportStopped() {
            if (this.report.statusId) {
                return this.report.statusId == 90
            }

            return false
        },
        hasSameWorkspace() {
            return this.report.productionSiteId === this.workspaceId
        },
        canShipOrder() {
            return this.allowsSendingOrders
        },
        isWMOrder() {
            return this.orderNumber && this.orderNumber.startsWith('WMD')
        },
        hasLieferscheinPrinter() {
            const workspace = JSON.parse(localStorage.getItem('workspace'))

            return workspace && workspace.lieferscheinPrinter
        },
        // validations
        machineStuffAmountErrors() {
            const errors = []

            if (!this.$v.machineStuffAmount.$dirty) return errors

            if (!this.$v.machineStuffAmount.required || !this.$v.machineStuffAmount.minValue) {
                errors.push('Proszę podać ilość osób.')
            }

            return errors
        },
        reportState() {
            let currentReportState = 0

            if (this.report.statusId === 1 && this.report.makeReadyStart) {
                if (!this.report.makeReadyFinish) {
                    currentReportState = this.currentReportStates[1]
                } else {
                    currentReportState = this.currentReportStates[3]
                }
            } else if (this.report.statusId === 90 && this.report.makeReadyStart) {
                if (!this.report.makeReadyFinish) {
                    currentReportState = this.currentReportStates[2]
                } else {
                    currentReportState = this.currentReportStates[4]
                }
            }

            return currentReportState
        },
        currentlyReporting() {
            if (this.report.statusId === 1 || this.report.statusId === 90) {
                if (this.report.makeReadyStart && !this.report.makeReadyFinish) {

                    return 'Zakończ przyrząd'
                }
            }

            return 'Zakończ raport'
        },
        workspaceSettings() {
            const workspace = localStorage.getItem('workspace')

            return JSON.parse(workspace)
        },
        isOffsetPrintSite() {
            return this.productionSiteData && this.productionSiteData.typeId === 7
        },
        reportSettings() {
            return this.$store.state.reports.reporting.reportSettings
        },
    },
    watch: {
        reportData() {
            this.report = JSON.parse(JSON.stringify(this.reportData))
            this.machineStuffAmount = JSON.parse(JSON.stringify(this.machineStuffAmountData))
            this.maxReportAmount = JSON.parse(JSON.stringify(this.maxReportAmountData))
            this.currentlyReportedQuantity = JSON.parse(JSON.stringify(this.currentlyReportedQuantityData))
            this.blackBoxCounterOn = this.report.blackBoxCounterOn

        }
    },
    created() {
        document.addEventListener('keydown', this.QRtoJSON)
    },
    destroyed() {
        document.removeEventListener('keydown', this.QRtoJSON)
        EventBus.$off()
    },
    methods: {
        pauseResumeBlackBoxCounting(bool) {
            const payload = {
                blackBoxCounterOn: bool,
                reportId: this.report.id
            }

            this.$store.dispatch('reports/pauseResumeBlackBoxCounting', payload)
                .then(response => {
                    this.blackBoxCounterOn = response.blackBoxCounterOn
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showCreateAccidentDialog() {
            this.accident.machineId = this.report.machine
            this.actionLoading.createAccident = true

            Promise.all([
                this.$store.dispatch('maintenance/getAccidentTypes'),
                this.$store.dispatch('maintenance/getMachines'),
                this.$store.dispatch('maintenance/getAccidentDepartments'),
                this.$store.dispatch('maintenance/getSoftwareTypes')
            ])
                .then(() => {
                    this.createAccidentDialog = true
                    this.reportBreakCommentCodeDialog = false
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.createAccident = false
                    this.actionLoading.createBreakReportCommentCode = false
                })

            addSentryBreadcrumb('ui.action', 'Zgłoszenie awarii')
        },
        closeCreateAccidentDialog() {
            this.createAccidentDialog = false
            this.reportBreakCommentCodeDialog = true

            setTimeout(() => {
                this.accident.id = null
                this.accident.accidentTypeId = null
                this.accident.machineId = null
                this.accident.description = null
                this.accident.username = null
                this.accident.accidentDepartmentId = null
            }, 500)
        },
        createAccident() {
            this.actionLoading.createAccident = true
            this.report.comments.push({ commentCodeId: this.selectedComment.code })
            this.accident.username = this.userName


            this.$store.dispatch('maintenance/createAccident', this.accident)
                .then(response => {
                    this.stopReport(false)
                        .then(() => {
                            if (response.data.accidentTypeId === 1) { // Stop maszyny
                                const accidentProcessId = 5

                                this.$store.dispatch('reports/createReport', {
                                    objectType: 3, // Inne zadanie
                                    objectId: accidentProcessId,
                                    objectNumber: '-',
                                    processCodeId: accidentProcessId,
                                    machine: this.report.machine,
                                    user: this.userId,
                                    currentlyReportedQuantity: 0,
                                    productionSiteId: this.workspaceId
                                })
                                    .then(response => {
                                        this.$router.push({
                                            name: 'reportsReportingReport', //TODO: Sprawdzić czy działa
                                            params: {
                                                id: response.id,
                                            }
                                        })
                                            .catch(error => {
                                                errorHandler(error)
                                            })
                                            .finally(() => {
                                                this.createAccidentDialog = false
                                                this.actionLoading.createAccident = false
                                            })
                                    })
                                    .catch(error => errorHandler(error))
                                    .finally(() => {
                                        this.createAccidentDialog = false
                                        this.actionLoading.createAccident = false
                                    })

                            } else {
                                this.$router.push({ name: this.routeParentName })
                                    .catch(error => {
                                        errorHandler(error)
                                    })
                                    .finally(() => {
                                        this.stopReportLoading = false
                                        this.actionLoading.createAccident = false
                                        this.createAccidentDialog = false
                                    })

                            }
                        })
                        .catch(error => {
                            this.createAccidentDialog = false
                            this.actionLoading.createAccident = false
                            errorHandler(error)
                        })
                })
                .catch(error => {
                    this.createAccidentDialog = false
                    this.actionLoading.createAccident = false
                    errorHandler(error)
                })

        },
        stopTracking() {
            this.codeTypeId = null
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                this.currentlyReportedQuantity = this.isReportStopped ? 0 : this.report.reportAmount

                if (this.report.reportAmount >= this.report.leftAmount && (!this.delay || this.timeWithoutLimit)) {
                    this.finishReport()
                } else {
                    if (this.report.reportAmount >= this.report.leftAmount && this.delay) {
                        this.codeTypeId = 1
                    }

                    if (this.report.reportAmount < this.report.leftAmount && !this.delay) {
                        this.codeTypeId = 2
                    }

                    this.showFinishReportCommentCodeDialog()
                    addSentryBreadcrumb('ui.action', 'Dodawanie komentarzy')
                }
            }
        },
        closeConfirmFinishReportDialog() {
            this.confirmFinishReportDialog = false
            this.amountToReportDialog = true
            this.amountToReportAction = 'finishReport'
        },
        redirectToCreateReport(task) {
            this.$router.push({
                name: 'ReportsReportingCreate',
                params: {
                    task: task,
                    plate: this.currentPlate,
                    outOfSequenceCodeId: null
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        redirectToCreatePrintReport(task) {
            this.$router.push({
                name: 'ReportsReportingCreatePrint',
                params: {
                    task: task,
                    plate: this.currentPlate,
                    outOfSequenceCodeId: null
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createReport() {
            this.actionLoading.createReport = true
            this.$store.dispatch('reports/getTask', {
                taskId: this.report.objectDetail.reportItemId,
                processId: this.report.process
            })
                .then(task => {
                    this.currentPlate = task.reportItemPlates.find(item => !item.isFullReported)
                    this.isOffsetPrintSite
                        ? this.redirectToCreatePrintReport(task)
                        : this.redirectToCreateReport(task)

                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.createReport = false
                })

        },
        finishReport() {
            this.$store.dispatch('toggleCircularLoader', true)

            this.confirmFinishReportDialog = false
            this.reportFinishCommentCodeDialog = false
            this.amountToReportDialog = false
            this.amountToReportAction = null
            this.stopTrackingLoading = true

            this.$store.dispatch('reports/finishReport', {
                id: this.report.id,
                payload: {
                    objectId: this.report.objectId,
                    objectType: this.report.objectType,
                    objectNumber: this.report.objectNumber,
                    machine: this.report.machine,
                    process: this.report.process,
                    user: this.report.user,
                    reportAmount: this.report.reportAmount,
                    machineStuffAmount: this.machineStuffAmount,
                    comments: this.report.comments,
                    statusId: this.statusId,
                    currentlyReportedQuantity: this.currentlyReportedQuantity,
                    productionSiteId: this.report.productionSiteId,
                    additionalActionProcessProductionSites: JSON.parse(JSON.stringify(this.additionalActionProcessProductionSite))
                }
            })
                .then(async response => {
                    if (
                        this.isFromReporting &&
                        response.data.objectType === 1 &&
                        response.data.leftAmount <= 0
                    ) {
                        if (this.isOffsetPrintSite) {
                            this.$store.dispatch('toggleCircularLoader', true)
                            await this.$store.dispatch('reports/getNextPlateToReport', {
                                processId: this.report.process,
                                productionSiteId: this.report.productionSiteId,
                                previouslyReportedPrimaryNumber: this.report.objectNumber
                            })
                                .then(response => {
                                    this.handleNextPlateToReport(response)
                                })
                        } else {
                            this.$store.dispatch('toggleCircularLoader', true)
                            this.$store.dispatch('reports/getNextPlatesToReport', {
                                taskNumber: this.report.objectNumber,
                                processId: this.report.process,
                                typeId: this.report.objectDetail.reportItemType,

                            })
                                .then(response => {
                                    this.handleUnreportedPlates(response.data.results)
                                })
                                .catch(error => {
                                    errorHandler(error)
                                })
                                .finally(() => {
                                    this.$store.dispatch('toggleCircularLoader', false)
                                    this.stopTrackingLoading = false
                                })
                        }
                    }
                    else {
                        if (this.isOffsetPrintSite) {
                            await this.$store.dispatch('reports/getNextPlateToReport', {
                                processId: this.report.process,
                                productionSiteId: this.report.productionSiteId,
                                previouslyReportedPrimaryNumber: this.report.objectNumber
                            })
                                .then(response => {
                                    this.$store.dispatch('toggleCircularLoader', false)
                                    this.handleNextPlateToReport(response)
                                })
                        } else {
                            this.$router.push({ name: this.routeParentName })
                                .catch(error => {
                                    errorHandler(error)
                                })
                                .finally(() => {
                                    this.stopTrackingLoading = false
                                    this.reopenAmountToReport = false
                                })
                        }
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$store.dispatch('toggleCircularLoader', false)
                    this.stopTrackingLoading = false
                    this.reopenAmountToReport = false
                })
        },
        handleNextPlateToReport(data) {
            if (data.reportItemId === null) {
                return this.$router.push({ name: this.routeParentName })
                    .catch(error => {
                        errorHandler(error)
                    })
            }

            this.offsetNextPlateToReport = data
            this.openOffsetNextPlateToReportDialog = true
        },
        closeOffsetNextPlateToReportDialog() {
            this.openOffsetNextPlateToReportDialog = false

            return this.$router.push({ name: this.routeParentName })
                .catch(error => {
                    errorHandler(error)
                })
        },
        async handleOffsetNextPlateToReportYesAction() {
            this.$store.dispatch('toggleCircularLoader', true)
            this.openOffsetNextPlateToReportDialog = false
            this.actionLoading.createReport = true

            const typesList = this.reportSettings.workspace.allowedReportItemTypes.map(item => {
                return item.reportItemType.id
            })

            const typeId = typesList.join()

            let taskId

            await this.$store.dispatch('reports/findTask', {
                taskNumber: this.offsetNextPlateToReport.primaryNumber,
                typeId: typeId
            })
                .then(task => {
                    taskId = task?.data?.results[0]?.id
                })
                .catch(error => {
                    this.$store.dispatch('toggleCircularLoader', false)
                    errorHandler(error)

                    return
                })
            await this.$store.dispatch('reports/getTask', {
                taskId: taskId,
                processId: this.report.process
            })
                .then(task => {
                    this.currentPlate = task.reportItemPlates.find(item => !item.isFullReported)
                    this.redirectToCreatePrintReportOffset(task)
                })
                .catch(error => {
                    this.$store.dispatch('toggleCircularLoader', false)
                    errorHandler(error)
                })
        },
        redirectToCreatePrintReportOffset(task) {
            this.$router.push({
                name: 'ReportsReportingCreatePrint',
                params: {
                    task: task,
                    plate: this.currentPlate,
                    outOfSequenceCodeId: null
                }
            })
        },
        async finishReportLastStep(navigateToNextScheduledTask) {
            this.$store.dispatch('toggleCircularLoader', true)
            if (navigateToNextScheduledTask) {

                const typesList = this.reportSettings.workspace.allowedReportItemTypes.map(item => {
                    return item.reportItemType.id
                })

                const typeId = typesList.join()

                let taskId

                await this.$store.dispatch('reports/findTask', {
                    taskNumber: this.report.nextScheduledTask.procNumber,
                    typeId: typeId
                })
                    .then(task => {
                        taskId = task?.data?.results[0]?.id
                    })
                    .catch(error => {
                        errorHandler(error)

                        return
                    })
                    .finally(() => {
                        this.$store.dispatch('toggleCircularLoader', false)
                    })

                this.$store.dispatch('reports/getTask', {
                    taskId: taskId,
                    processId: this.report.process
                })
                    .then(task => {
                        this.currentPlate = task.reportItemPlates.find(item => !item.isFullReported)
                        this.isOffsetPrintSite
                            ? this.redirectToCreatePrintReport(task)
                            : this.redirectToCreateReport(task)

                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.$store.dispatch('toggleCircularLoader', false)
                    })

            } else {
                this.$router.push({ name: this.routeParentName })
                this.$store.dispatch('toggleCircularLoader', false)
            }
            this.$store.dispatch('toggleCircularLoader', false)
        },
        async stopReport(returnToSamePage = true) {
            this.confirmFinishReportDialog = false
            this.reportBreakCommentCodeDialog = false
            this.stopReportLoading = true

            return await this.$store.dispatch('reports/finishReport', {
                id: this.report.id,
                payload: {
                    objectId: this.report.objectId,
                    objectType: this.report.objectType,
                    objectNumber: this.report.objectNumber,
                    machine: this.report.machine,
                    process: this.report.process,
                    user: this.report.user,
                    reportAmount: this.report.reportAmount,
                    machineStuffAmount: this.machineStuffAmount,
                    comments: this.report.comments,
                    statusId: this.statusId,
                    currentlyReportedQuantity: this.report.reportAmount,
                    productionSiteId: this.report.productionSiteId,
                    pauseMakeReady: this.report.pauseMakeReady,
                    additionalActionProcessProductionSites: JSON.parse(JSON.stringify(this.additionalActionProcessProductionSite))
                }
            })
                .then(() => {
                    if (returnToSamePage) {
                        this.$router.push({ name: this.routeParentName })
                            .catch(error => {
                                errorHandler(error)
                            })
                            .finally(() => {
                                this.stopReportLoading = false
                            })
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.stopReportLoading = false
                })
        },
        endStoppedReport() {
            this.statusId = 99
            this.stopTracking()
        },
        stopMakeReady() {
            this.statusId = 90
            this.report.reportAmount = 0
            this.report.pauseMakeReady = true
            this.showBreakReportCommentCodeDialog()
        },
        openEndMakeReadyDialog() {
            this.makeReadyAmountToReportDialog = true
        },
        renewReport(restartMakeReady = false) {
            this.renewReportLoading = true
            this.$store.dispatch('reports/renewReport', {
                id: this.report.id,
                payload: {
                    objectId: this.report.objectId,
                    objectType: this.report.objectType,
                    objectNumber: this.report.objectNumber,
                    machine: this.report.machine,
                    process: this.report.process,
                    user: this.report.user,
                    reportAmount: this.report.reportAmount,
                    machineStuffAmount: this.machineStuffAmount,
                    comments: this.report.comments,
                    statusId: 1,
                    currentlyReportedQuantity: 0,
                    productionSiteId: this.report.productionSiteId,
                    restartMakeReady: restartMakeReady,
                }
            })
                .then(response => {
                    this.$emit('update-renew-report', response.data)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.renewReportLoading = false
                })
        },
        createBreakReport() {
            this.actionLoading = true
            this.reportBreakCommentCodeDialog = false
            this.stopReport(false)
                .then(() => {
                    this.$store.dispatch('reports/createReport', {
                        processCodeId: 2, // Przerwa śniadaniowa
                        objectType: 3, // Inne zadanie
                        objectId: 2, // Przerwa śniadaniowa
                        objectNumber: '-',
                        machine: this.report.machine,
                        user: this.userId,
                        currentlyReportedQuantity: 0,
                        productionSiteId: this.workspaceId
                    })
                        .then(response => {
                            this.$router.push({
                                name: 'reportsReportingReport',
                                params: {
                                    id: response.id,
                                }
                            })
                                .catch(error => {
                                    errorHandler(error)
                                })
                        })
                        .catch(error => errorHandler(error))
                })
        },
        closeConfirmNextReportCreationDialog() {
            this.confirmNextReportCreationDialog = false
            this.nextUnreportedPlate = null
            this.$router.push({ name: this.routeParentName })
                .catch(error => {
                    errorHandler(error)
                })
        },
        handleUnreportedPlates(plates, enabled = true) {
            if (enabled && plates.length > 0) {
                this.$store.dispatch('toggleCircularLoader', false)
                this.confirmNextReportCreationDialog = true
                this.nextUnreportedPlate = plates[0]
                addSentryBreadcrumb('ui.action', 'Raportowanie kolejnego montażu')
            } else {
                if (this.report.nextScheduledTask && this.isOffsetPrintSite) {
                    this.chooseFinishStepDialog = true
                    this.$store.dispatch('toggleCircularLoader', false)
                } else {
                    this.$router.push({ name: this.routeParentName })
                        .catch(error => {
                            errorHandler(error)
                        })
                }
            }
        },
        handleQrJSON({ data }) {
            if (typeof data.event !== 'undefined' && data.event === 'stop') {
                this.showAmountToReportDialog(99)
            }
        },
        showBreakReportCommentCodeDialog() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.amountToReportDialog = false

            if (this.report.reportAmount >= this.report.leftAmount) {
                this.confirmFinishReportDialog = true
                this.statusId = 99
                addSentryBreadcrumb('ui.action', 'Potwierdzenie pełnego nakładu')
            } else {
                this.stopReportLoading = true
                this.$store.dispatch('reports/getCommentCodes', {
                    codeTypeId: this.codeTypeId = 3, productionSiteId: this.reportData.productionSiteId
                })
                    .then(response => {
                        this.commentCodes = response.data.results
                        this.reportBreakCommentCodeDialog = true
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.stopReportLoading = false
                    })
                addSentryBreadcrumb('ui.action', 'Dodanie komentarza')
            }
        },
        saveBreakReportCommentCode(payload) {
            this.report = payload.report
            if (payload.selectedComment.id === 5) {
                this.selectedComment = payload.selectedComment
                this.showCreateAccidentDialog()
                this.actionLoading.createBreakReportCommentCode = true
            }
            else if (payload.selectedComment.id === 6) {
                this.createBreakReport()
            }
            else {
                this.stopReport()
            }

        },
        closeBreakReportCommentCodeDialog() {
            this.reportBreakCommentCodeDialog = false
            this.showAmountToReportDialog(this.statusId)
        },
        showOrders() {
            this.$emit('show-next-step')
        },
        showAmountToReportDialog(statusId) {
            this.amountToReportDialog = true
            this.statusId = statusId
            this.reopenAmountToReport = true
        },
        closeAmountToReportDialog() {
            this.amountToReportDialog = false
            this.report.reportAmount = JSON.parse(JSON.stringify(this.reportData.reportAmount))
        },
        saveAmountToReport(data) {
            this.report = data
            this.statusId === 90 ? this.showBreakReportCommentCodeDialog() : this.stopTracking()
        },
        showFinishReportCommentCodeDialog() {
            this.amountToReportDialog = false
            const query = this.codeTypeId ? this.codeTypeId : '1,2' // Opóźnienie, Niepełny nakład

            this.$store.dispatch('reports/getCommentCodes', {
                codeTypeId: query,
                productionSiteId: this.reportData.productionSiteId
            })
                .then(response => {
                    this.commentCodes = response.data.results
                    this.reportFinishCommentCodeDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        saveFinishReportCommentCode(payload) {
            if (this.delay && this.codeTypeId === 2) {
                this.reportFinishCommentCodeDialog = false
                this.stopTracking()
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Zarejestrowano opóźnienie. Proszę ponownie wybrać powody.'
                })
            } else {
                this.report = payload

                this.finishReport()
            }
        },
        closeFinishReportCommentCodeDialog() {
            this.reportFinishCommentCodeDialog = false

            if (this.reopenAmountToReport) {
                this.amountToReportDialog = true
            }
        },
        showPalletsListDialog() {
            if (this.isOffsetPrintSite) {
                this.palletsListOffsetDialog = true
            } else {
                this.palletsListDialog = true
            }
        },
        closePalletsListDialog(pallets) {
            if (this.isOffsetPrintSite) {
                this.report.reportPallets = pallets
                this.palletsListOffsetDialog = false
            } else {
                this.palletsListDialog = false
            }
        },
        showOrderAdditionalBoardsDialog() {
            this.openOrderAdditionalBoardsDialog = true
        },
        closeOrderAdditionalBoardsDialog() {
            this.openOrderAdditionalBoardsDialog = false
        },
        handleFinishStepNoAction() {
            this.chooseFinishStepDialog = false
            this.finishReportLastStep(false)
        },
        handleFinishStepYesAction() {
            this.chooseFinishStepDialog = false
            this.finishReportLastStep(true)
        },
        closeMakeReadyAmountToReportDialog() {
            this.makeReadyAmountToReportDialog = false
        },
        saveMakeReadyAmountToReport(makeReadyAmount) {
            this.makeReadyAmountToReportDialog = false
            this.endMakeReady(makeReadyAmount)
        },
        endMakeReady(makeReadyAmount) {
            this.$store.dispatch('toggleCircularLoader', true)
            this.endMakeReadyLoading = true
            this.$store.dispatch('reports/endMakeReady', {
                id: this.report.id,
                payload: {
                    objectId: this.report.objectId,
                    objectType: this.report.objectType,
                    objectNumber: this.report.objectNumber,
                    machine: this.report.machine,
                    process: this.report.process,
                    user: this.report.user,
                    reportAmount: this.report.reportAmount,
                    machineStuffAmount: this.machineStuffAmount,
                    comments: this.report.comments,
                    statusId: 1,
                    currentlyReportedQuantity: 0,
                    productionSiteId: this.report.productionSiteId,
                    makeReadyAmount: makeReadyAmount,
                    finishMakeReady: true
                }
            })
                .then(response => {
                    this.$emit('update-end-make-ready-report', response.data)
                    if (this.isReportStopped) this.$emit('set-duration')
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.endMakeReadyLoading = false
                    this.$store.dispatch('toggleCircularLoader', false)
                })
        }
    },
}
</script>
